// Design Score: one cohesive 0-100 for the room, decomposed into the
// components that produced it, so changing any single factor visibly moves
// a specific bar rather than a mystery number.
//
// It is deliberately assembled from things the engine ALREADY computes -
// harmony, coherence findings, style fit, rule floors - rather than a new
// parallel opinion. That keeps one source of truth: if a rule weight changes
// in designRules.ts, the score moves with it.
//
// Honest framing: this measures INTERNAL CONSISTENCY against a declared
// look and the design laws in this engine. It is not a measure of whether a
// room is good. Taste, client wishes, budget, existing furniture, light
// quality, and the thousand things not modeled here all outrank it. The UI
// carries that disclaimer; the code carries it too, right here.

import type { StyleKey } from "./types.js";
import { styleFit } from "./style.js";
import { ruleWeight } from "./designRules.js";
import type { CoherenceReport } from "./coherence.js";

export interface ScoreComponent {
  key: "harmony" | "coherence" | "styleFit" | "livability" | "durability";
  label: string;
  score: number; // 0..1
  weight: number; // relative importance, from rule weights where applicable
  detail: string; // one line explaining this component's value
}

export interface DesignScore {
  total: number; // 0-100
  band: "excellent" | "strong" | "mixed" | "conflicted";
  components: ScoreComponent[];
  headline: string;
  styleLabel: string;
}

export interface ScoreInput {
  styleVibe: StyleKey;
  styleLabel: string;
  wall: { name: string; lrv: number; effTemp: number; chroma: number };
  floor: { name: string; lrv: number; effTemp: number; chroma: number };
  harmony: { gap: number; score: number } | null; // W-01 vs the floor
  coherence: CoherenceReport;
  lrvFloor: number; // the active L-01/L-02 floor (0 = none)
  wallMeetsLrvFloor: boolean;
  sheenBelowWetFloor: boolean;
  durabilityOk: boolean; // class-A line where the objective wants one
}

// The band is capped by the WEAKEST component, not just the average. A room
// can't read "excellent" while one element is genuinely broken - averaging
// would otherwise hide three clashing metals behind four good scores.
function band(total: number, weakest: number): DesignScore["band"] {
  let b: DesignScore["band"] =
    total >= 85 ? "excellent" : total >= 70 ? "strong" : total >= 50 ? "mixed" : "conflicted";
  const order: DesignScore["band"][] = ["conflicted", "mixed", "strong", "excellent"];
  const cap: DesignScore["band"] =
    weakest < 0.4 ? "mixed" : weakest < 0.65 ? "strong" : "excellent";
  return order.indexOf(b) > order.indexOf(cap) ? cap : b;
}

const BAND_COPY: Record<DesignScore["band"], string> = {
  excellent: "These choices reinforce each other.",
  strong: "A cohesive room with one or two things to reconcile.",
  mixed: "Workable, but several elements are pulling against each other.",
  conflicted: "These picks are fighting. Worth revisiting the flagged items.",
};

// Coherence findings cost the score in proportion to the weight of the rule
// they violate, so a metal clash (M-01, 9.4) hurts more than busyness
// (P-01, 6.5). Strong findings cost double a notable one.
function coherenceScore(report: CoherenceReport): { score: number; detail: string } {
  if (report.findings.length === 0) {
    return { score: 1, detail: "nothing competing: metals, wood tones, and focal points all agree" };
  }
  let penalty = 0;
  for (const f of report.findings) {
    const w = ruleWeight(f.rule) / 10; // 0..1
    penalty += w * (f.severity === "strong" ? 0.5 : 0.25);
  }
  const score = Math.max(0, 1 - penalty);
  const ids = report.findings.map((f) => f.rule).join(", ");
  return { score, detail: `${report.findings.length} clash${report.findings.length === 1 ? "" : "es"} flagged (${ids})` };
}

export function computeDesignScore(input: ScoreInput): DesignScore {
  const components: ScoreComponent[] = [];

  // 1. Harmony: does the wall sit right against the floor? (W-01)
  if (input.harmony) {
    components.push({
      key: "harmony", label: "Wall / floor harmony",
      score: input.harmony.score, weight: ruleWeight("W-01"),
      detail: `temperature gap ${input.harmony.gap} against the floor`,
    });
  }

  // 2. Coherence: is the room as a whole uncluttered? (M-01 / WD-01 / P-01)
  const coh = coherenceScore(input.coherence);
  components.push({
    key: "coherence", label: "Room coherence",
    score: coh.score, weight: ruleWeight("M-01"), detail: coh.detail,
  });

  // 3. Style fit: do the two big surfaces read as the declared look?
  const wallFit = styleFit(
    { name: input.wall.name, lrv: input.wall.lrv, temperature: input.wall.effTemp, chroma: input.wall.chroma },
    input.styleVibe, "wall");
  const floorFit = styleFit(
    { name: input.floor.name, lrv: input.floor.lrv, temperature: input.floor.effTemp, chroma: input.floor.chroma },
    input.styleVibe, "floor");
  const fit = (wallFit + floorFit) / 2;
  components.push({
    key: "styleFit", label: `Reads as ${input.styleLabel}`,
    score: fit, weight: 7.0,
    detail: `walls ${(wallFit * 100).toFixed(0)}%, floor ${(floorFit * 100).toFixed(0)}% on brief`,
  });

  // 4. Livability: LRV floors for low light and small rooms (L-01 / L-02)
  const livable = input.lrvFloor === 0 ? 1 : input.wallMeetsLrvFloor ? 1 : 0.45;
  components.push({
    key: "livability", label: "Light and livability",
    score: livable, weight: ruleWeight("L-01"),
    detail: input.lrvFloor === 0
      ? "no LRV floor applies in this room"
      : input.wallMeetsLrvFloor
      ? `clears the LRV ${input.lrvFloor} floor for this light`
      : `sits below the LRV ${input.lrvFloor} floor: expect a dimmer room`,
  });

  // 5. Durability: the right product class and a sheen that survives the room
  const durable = (input.durabilityOk ? 0.6 : 0.25) + (input.sheenBelowWetFloor ? 0 : 0.4);
  components.push({
    key: "durability", label: "Durability and upkeep",
    score: Math.min(1, durable), weight: ruleWeight("R-01"),
    detail: input.sheenBelowWetFloor
      ? "sheen sits below the wet-room floor: harder to clean"
      : input.durabilityOk
      ? "scrubbable finish appropriate to the room"
      : "finish class is adequate but not the toughest option",
  });

  const wsum = components.reduce((s, c) => s + c.weight, 0);
  const vsum = components.reduce((s, c) => s + c.score * c.weight, 0);
  const total = Math.round(100 * (vsum / wsum));

  const weakest = [...components].sort((a, b) => a.score - b.score)[0];
  const b = band(total, weakest.score);
  const headline = b === "excellent"
    ? BAND_COPY[b]
    : `${BAND_COPY[b]} Weakest link: ${weakest.label.toLowerCase()}.`;

  return { total, band: b, components, headline, styleLabel: input.styleLabel };
}
